import { useEffect, useState } from 'react';
import type { Phase } from '@ringmaster/shared';

/**
 * Per-phase mood: background gradient + accent color + glow for each phase.
 * Night deepens to crimson-black (#000 → #320001); dawn/death flashes blood;
 * lobby/day/end sit in warmer, softer tints. A4/A6 (Wave 1) consume this; values
 * derive from the design tokens (theme/tokens.css).
 */

export interface PhaseTheme {
  /** CSS background value (gradient). */
  bgGradient: string;
  /** Accent color (hex) for highlights on this phase. */
  accent: string;
  /** Box-shadow glow string for this phase. */
  glow: string;
  /**
   * v2 (AAA makeover, additive/back-compatible) — the scene key this phase maps
   * to in a game's SceneManifest (see src/assets/scenes/). Optional so existing
   * consumers of PhaseTheme are unaffected; SceneStack derives the scene from the
   * manifest's own phaseToScene map, so this is a convenience mirror for screens
   * that want the scene key without threading the manifest.
   */
  scene?: string;
}

const NIGHT: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #320001 100%)',
  accent: '#990000',
  glow: '0 0 80px rgba(153,0,0,.35)',
};

/**
 * The dawn/reveal treatment for LIVING players + the host view: near-black with
 * the reveal text. The BLOOD-red flash is reserved for the player who actually
 * died (handled directly in the hand DawnScreen / DeadScreen), so at dawn only
 * the dead player's phone goes blood-red — everyone else stays calm near-black.
 */
const DAWN_REVEAL: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #0d0507 100%)',
  accent: '#802636',
  glow: '0 0 40px rgba(128,38,54,.15)',
};

const WARM: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #1a0f08 100%)',
  accent: '#7e5c44',
  glow: 'none',
};

const NEUTRAL: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #140002 100%)',
  accent: '#802636',
  glow: '0 0 40px rgba(128,38,54,.2)',
};

const WOLF_WIN: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #320001 100%)',
  accent: '#990000',
  glow: '0 0 80px rgba(153,0,0,.45)',
};

const VILLAGE_WIN: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #1a1000 100%)',
  accent: '#c9a44e',
  glow: '0 0 60px rgba(201,164,78,.2)',
};

// ── The Whisper — arcana night skin (violet candle glow, see designSystem arcana
//    ramp: --arcana-0 #160b26 / --arcana-1 #2c1650 / --arcana-3 #8b5cf6). Distinct
//    from Blood Moon's crimson NIGHT/NEUTRAL/DAWN_REVEAL tints below.
const ARCANA_NIGHT: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #160b26 100%)',
  accent: '#8b5cf6',
  glow: '0 0 80px rgba(139,92,246,.3)',
};

/** HUNT — the accusation vote; a hotter violet than the ambient séance tint. */
const ARCANA_HUNT: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #1e0f34 100%)',
  accent: '#a78bfa',
  glow: '0 0 90px rgba(167,139,250,.4)',
};

/** RESOLVE_HUNT / SCORE — the reveal settling back toward calm arcana ink. */
const ARCANA_REVEAL: PhaseTheme = {
  bgGradient: 'linear-gradient(180deg, #080808 0%, #1a0f2e 100%)',
  accent: '#5b3fa8',
  glow: '0 0 40px rgba(91,63,168,.2)',
};

export const PHASE_THEME: Record<Phase, PhaseTheme> = {
  LOBBY: WARM,
  DEAL: NEUTRAL,
  NIGHT_INTRO: NIGHT,
  NIGHT_WOLVES: NIGHT,
  NIGHT_INTERLUDE: NIGHT,
  NIGHT_SEER: NIGHT,
  RESOLVE_NIGHT: NIGHT,
  // Dawn/lynch reveals: calm near-black for the living + host. Only the DEAD
  // player's own screen turns blood-red (see hand DawnScreen / DeadScreen).
  DAWN: DAWN_REVEAL,
  DISCUSS: WARM,
  VOTE: NEUTRAL,
  RESOLVE_VOTE: DAWN_REVEAL,
  // ── The Whisper — arcana night skin (violet candle glow, not Blood Moon's
  //    crimson). HUNT (accusation vote) runs slightly hotter than the ambient
  //    séance tint; RESOLVE_HUNT/SCORE settle back toward calm arcana ink.
  WHISPER_INTRO: ARCANA_NIGHT,
  QUESTION: ARCANA_NIGHT,
  RESOLVE_GUESS: ARCANA_NIGHT,
  HUNT: ARCANA_HUNT,
  RESOLVE_HUNT: ARCANA_REVEAL,
  SCORE: ARCANA_REVEAL,
  END: WARM, // overridden at runtime by winner team
  // ── One Night — a Blood Moon variant (crimson skin, not arcana). One
  // compact night reuses NIGHT for every wake beat; dawn/discuss/vote mirror
  // the Blood Moon phases they correspond to.
  ONENIGHT_NIGHTFALL: NIGHT,
  ONENIGHT_NIGHT_ACTIONS: NIGHT,
  ONENIGHT_DAWN: DAWN_REVEAL,
  ONENIGHT_DISCUSS: WARM,
  ONENIGHT_VOTE: NEUTRAL,
  ONENIGHT_RESOLVE: DAWN_REVEAL,
};

export function themeForPhase(phase: Phase): PhaseTheme {
  return PHASE_THEME[phase];
}

/** Winner-aware variant for the END phase */
export function themeForEnd(winner: 'VILLAGE' | 'WOLVES'): PhaseTheme {
  return winner === 'WOLVES' ? WOLF_WIN : VILLAGE_WIN;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * usePhaseTheme(phase) — returns {bgGradient, accent, glow} for the current phase.
 * No side-effects; purely derives from the phase map.
 */
export function usePhaseTheme(phase: Phase): PhaseTheme {
  return PHASE_THEME[phase];
}

/**
 * Reads the --duration-phase CSS variable and returns it in seconds.
 * Used by PhaseBackground for the framer-motion crossfade duration,
 * so prefers-reduced-motion collapses to 0.
 */
export function usePhaseDuration(): number {
  const [duration, setDuration] = useState(0.8);
  useEffect(() => {
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue('--duration-phase')
      .trim();
    const ms = parseFloat(val);
    if (!isNaN(ms)) setDuration(ms / 1000);
  }, []);
  return duration;
}
